<script setup lang="ts">
/**
 * A modifier group — "Příloha", "Omáčka", "Bez" — and its options.
 *
 * The rule is said in words a manager uses: "must choose at least", "can
 * choose at most". Saving replaces the option list whole, in the order shown;
 * an option removed here is switched off rather than deleted, so last week's
 * receipts still name it.
 */
import { computed, reactive } from "vue";
import type { DraftGroup, GroupInput } from "../api";
import type { Translations } from "@gf/contracts/values";

const props = defineProps<{
  /** Null creates a new group. */
  group: DraftGroup | null;
  t: (key: string) => string;
  busy: boolean;
  /** How many items offer it, to say what an edit reaches. */
  usedBy: number;
}>();

const emit = defineEmits<{
  save: [input: GroupInput];
  cancel: [];
}>();

const toText = (minor: bigint): string => {
  const negative = minor < 0n;
  const s = (negative ? -minor : minor).toString().padStart(3, "0");
  return `${negative ? "-" : ""}${s.slice(0, -2)},${s.slice(-2)}`;
};

interface Row {
  optionId?: string;
  name: string;
  price: string;
  maxQuantity: number;
  /** For a guest reading the menu in English; other languages are kept as they were. */
  nameEn: string;
  translations?: Translations;
}

const form = reactive({
  name: props.group?.name ?? "",
  minSelections: props.group?.minSelections ?? 0,
  /** Empty is "no limit". */
  maxSelections: props.group?.maxSelections === null || props.group === null ? "" : String(props.group.maxSelections),
  active: props.group?.active ?? true,
  nameEn: props.group?.translations?.en?.name ?? "",
  rows: (props.group?.options.filter((o) => o.active) ?? []).map(
    (o): Row => ({
      optionId: o.optionId,
      name: o.name,
      price: toText(o.priceDeltaMinor),
      maxQuantity: o.maxQuantity,
      nameEn: o.translations?.en?.name ?? "",
      ...(o.translations ? { translations: o.translations } : {}),
    }),
  ),
});
if (form.rows.length === 0) form.rows.push({ name: "", price: "0,00", maxQuantity: 1, nameEn: "" });

/**
 * English as typed, over whatever else the venue already had — and nothing at
 * all when there are no words in any language, so a group nobody translated
 * is saved exactly as it always was.
 */
const withEnglish = (kept: Translations | undefined, name: string): { translations?: Translations } => {
  const { en: _old, ...others } = kept ?? {};
  const merged: Translations = { ...others, ...(name.trim() ? { en: { ...(kept?.en ?? {}), name: name.trim() } } : {}) };
  return Object.keys(merged).length > 0 || kept?.en ? { translations: merged } : {};
};

const minor = (text: string): bigint | null => {
  const cleaned = text.replace(/\s/g, "").replace(",", ".");
  if (!/^-?\d{1,9}(\.\d{1,2})?$/.test(cleaned)) return null;
  const negative = cleaned.startsWith("-");
  const [whole, frac = ""] = cleaned.replace("-", "").split(".");
  const value = BigInt(`${whole!}${frac.padEnd(2, "0")}`);
  return negative ? -value : value;
};

const max = computed<number | null>(() => (form.maxSelections.trim() === "" ? null : Number(form.maxSelections)));

const problem = computed<string | null>(() => {
  if (form.name.trim() === "") return "menu.problem.name";
  if (form.rows.length === 0 || form.rows.some((r) => r.name.trim() === "")) return "menu.problem.optionName";
  if (form.rows.some((r) => minor(r.price) === null)) return "menu.problem.price";
  if (form.minSelections > form.rows.length) return "menu.problem.tooFew";
  if (max.value !== null && (!Number.isInteger(max.value) || max.value < Math.max(form.minSelections, 1))) {
    return "menu.problem.max";
  }
  return null;
});

const move = (from: number, by: number): void => {
  const to = from + by;
  if (to < 0 || to >= form.rows.length) return;
  const [row] = form.rows.splice(from, 1);
  form.rows.splice(to, 0, row!);
};

const save = (): void => {
  if (problem.value !== null) return;
  emit("save", {
    name: form.name.trim(),
    minSelections: form.minSelections,
    maxSelections: max.value,
    active: form.active,
    ...withEnglish(props.group?.translations, form.nameEn),
    options: form.rows.map((r) => ({
      ...(r.optionId ? { optionId: r.optionId } : {}),
      name: r.name.trim(),
      priceDeltaMinor: minor(r.price)!,
      maxQuantity: r.maxQuantity,
      ...withEnglish(r.translations, r.nameEn),
    })),
  });
};
</script>

<template>
  <aside class="gf-drawer sheet" role="dialog" aria-modal="false" :aria-label="group?.name ?? t('menu.newGroup')">
    <button type="button" class="gf-drawer__close" :aria-label="t('menu.cancel')" @click="emit('cancel')">
      ×
    </button>
    <h2 class="gf-drawer__title">{{ group ? group.name : t("menu.newGroup") }}</h2>
    <p v-if="group" class="gf-muted">{{ t("menu.usedBy").replace("{count}", String(usedBy)) }}</p>

    <form class="sheet__form" @submit.prevent="save">
      <label class="gf-field">
        <span class="gf-label">{{ t("menu.field.name") }}</span>
        <input v-model="form.name" class="gf-input" type="text" maxlength="80" data-field="group-name" />
      </label>

      <div class="sheet__pair">
        <label class="gf-field">
          <span class="gf-label">{{ t("menu.field.min") }}</span>
          <input v-model.number="form.minSelections" class="gf-input gf-num" type="number" min="0" data-field="min" />
        </label>
        <label class="gf-field">
          <span class="gf-label">{{ t("menu.field.max") }}</span>
          <input
            v-model="form.maxSelections"
            class="gf-input gf-num"
            type="text"
            inputmode="numeric"
            :placeholder="t('menu.field.maxUnlimited')"
            data-field="max"
          />
        </label>
      </div>

      <fieldset class="sheet__fieldset">
        <legend class="gf-label">{{ t("menu.field.options") }}</legend>
        <div class="sheet__rows">
          <div class="sheet__row sheet__row--head" aria-hidden="true">
            <span>{{ t("menu.field.optionName") }}</span>
            <span>{{ t("menu.field.priceDelta") }}</span>
            <span>{{ t("menu.field.maxQuantity") }}</span>
            <span />
          </div>
          <div v-for="(row, i) in form.rows" :key="row.optionId ?? `new-${i}`" class="sheet__row" data-option-row>
            <input
              v-model="row.name"
              class="gf-input"
              type="text"
              maxlength="80"
              :aria-label="t('menu.field.optionName')"
            />
            <input
              v-model="row.price"
              class="gf-input gf-num"
              type="text"
              inputmode="decimal"
              :aria-label="t('menu.field.priceDelta')"
            />
            <input
              v-model.number="row.maxQuantity"
              class="gf-input gf-num"
              type="number"
              min="1"
              max="9"
              :aria-label="t('menu.field.maxQuantity')"
            />
            <span class="sheet__rowActions">
              <button type="button" class="gf-button gf-button--ghost" :aria-label="t('menu.moveUp')" @click="move(i, -1)">↑</button>
              <button type="button" class="gf-button gf-button--ghost" :aria-label="t('menu.moveDown')" @click="move(i, 1)">↓</button>
              <button
                type="button"
                class="gf-button gf-button--ghost"
                :aria-label="t('menu.remove')"
                :disabled="form.rows.length === 1"
                @click="form.rows.splice(i, 1)"
              >
                ×
              </button>
            </span>
          </div>
        </div>
        <button
          type="button"
          class="gf-button sheet__add"
          data-testid="add-option"
          @click="form.rows.push({ name: '', price: '0,00', maxQuantity: 1, nameEn: '' })"
        >
          {{ t("menu.addOption") }}
        </button>
      </fieldset>

      <fieldset class="sheet__fieldset sheet__english" data-section="english">
        <legend class="gf-label">{{ t("menu.nameEn") }}</legend>
        <label class="gf-field">
          <span class="sheet__hint">{{ form.name || t("menu.field.name") }}</span>
          <input v-model="form.nameEn" class="gf-input" type="text" maxlength="80" data-field="group-name-en" />
        </label>
        <label v-for="(row, i) in form.rows" :key="`en-${row.optionId ?? i}`" class="gf-field">
          <span class="sheet__hint">{{ row.name || t("menu.field.optionName") }}</span>
          <input v-model="row.nameEn" class="gf-input" type="text" maxlength="80" :data-field="`option-name-en-${i}`" />
        </label>
      </fieldset>

      <label class="sheet__check">
        <input v-model="form.active" type="checkbox" />
        {{ t("menu.field.active") }}
      </label>

      <p v-if="problem" class="gf-muted" role="status">{{ t(problem) }}</p>

      <div class="sheet__actions">
        <button type="button" class="gf-button" @click="emit('cancel')">{{ t("menu.cancel") }}</button>
        <button
          type="submit"
          class="gf-button gf-button--primary"
          :disabled="problem !== null || busy"
          data-testid="save-group"
        >
          {{ t("menu.save") }}
        </button>
      </div>
    </form>
  </aside>
</template>

<style scoped>
.sheet__hint {
  font-size: var(--gf-text-xs);
  color: var(--gf-color-text-muted);
}

.sheet__english {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-2);
}

.sheet {
  width: min(38rem, 100vw);
}

.sheet__form {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-4);
}

.sheet__pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--gf-space-3);
}

.sheet__fieldset {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-2);
  margin: 0;
  padding: 0;
  border: 0;
}

.sheet__rows {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-1);
}

.sheet__row {
  display: grid;
  grid-template-columns: 1fr 7rem 5rem auto;
  gap: var(--gf-space-2);
  align-items: center;
}

.sheet__row--head {
  font-size: var(--gf-text-xs);
  color: var(--gf-color-text-muted);
}

.sheet__rowActions {
  display: flex;
  gap: var(--gf-space-0);
}

.sheet__rowActions .gf-button {
  min-height: 32px;
  min-width: 32px;
  padding: 0;
}

.sheet__add {
  align-self: flex-start;
}

.sheet__check {
  display: flex;
  align-items: center;
  gap: var(--gf-space-2);
}

.sheet__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--gf-space-2);
  padding-top: var(--gf-space-4);
  border-top: var(--gf-border-thin) solid var(--gf-color-border-subtle);
}
</style>
